import {useEffect, useRef, useState} from 'react'

export type StateModifier<TState> = (state: TState) => TState

export class StateStore<TState> {
  public state: TState
  // Множество подписчиков, которым передаётся новое состояние при изменениях
  private subscribers = new Set<(state: TState) => void>()

  constructor(initialState: TState) {
    this.state = initialState
  }

  /**
   * Объединяет новое состояние с существующим
   * @param newState
   */
  public assignState(newState: Partial<TState>) {
    this.state = {
      ...this.state,
      ...newState
    }
    this.notify()
  }

  /**
   * Заменяет текущее состояние новым (либо через функцию-модификатор)
   * @param newState
   */
  public setState(newState: TState | StateModifier<TState>) {
    if (typeof newState === 'function') {
      this.state = (newState as StateModifier<TState>)(this.state)
    } else {
      this.state = newState
    }
    this.notify()
  }

  /**
   * Подписывается на изменения состояния.
   * @param handler Функция-обработчик изменений состояния
   * @param fireImmediately Если true – сразу вызывает handler с текущим состоянием
   * @returns Функция отписки
   */
  public subscribe(
    handler: (state: TState) => void,
    fireImmediately = false
  ): () => void {
    if (fireImmediately) {
      handler(this.state)
    }
    this.subscribers.add(handler)
    return () => {
      this.subscribers.delete(handler)
    }
  }

  /**
   * Уведомляет всех подписчиков об изменении состояния.
   */
  private notify() {
    for (const subscriber of this.subscribers) {
      subscriber(this.state)
    }
  }

  /**
   * Хук для использования состояния в функциональных компонентах.
   * @example
   * const [state, setState] = store.useState();
   */
  public useState(): [
    TState,
    (newState: TState | StateModifier<TState>) => void
  ] {
    const [localState, setLocalState] = useState(this.state)

    useEffect(() => {
      const unsubscribe = this.subscribe(setLocalState)
      return unsubscribe
    }, [])

    return [localState, this.setState.bind(this)]
  }

  /**
   * Хук для выбора части состояния. Данный хук игнорирует сам объект функции как элемент зависимости, для явного указывания зависимости используйте массив зависимостей deps.
   */
  public useSelector<T>(selector: (state: TState) => T, deps: any[] = []): T {
    const [selectedState, setSelectedState] = useState(() =>
      selector(this.state)
    )
    const selectorRef = useRef(selector)
    const initialRef = useRef(true)
    selectorRef.current = selector

    useEffect(() => {
      if (initialRef.current) {
        initialRef.current = false
        return
      }
      setSelectedState(selector(this.state))
    }, deps)

    useEffect(() => {
      let prevSelected = selectedState
      const unsubscribe = this.subscribe(newState => {
        const nextSelected = selectorRef.current(newState)
        if (prevSelected !== nextSelected) {
          prevSelected = nextSelected
          setSelectedState(nextSelected)
        }
      })
      return unsubscribe
    }, [])

    return selectedState
  }
}
